import React from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import classes from "./AnswerFile.module.scss";
import { useImageUploader } from "../../../../../../hooks/useImageUploader";
import { UPLOAD_MEDIA_TYPE } from "../../../../../../api/mutations/useUploadMediaMutation";
import { ImageUploader } from "../../../../../../boxes/ImageUploader/ImageUploader";

export function AnswerFile({
  isRequired,
  initialValues,
  renderFooter,
  onSubmit,
}) {
  const isUploadProcessingRef = React.useRef(false);
  const uploader = useImageUploader({
    uploadType: UPLOAD_MEDIA_TYPE.questionnaires,
    includeExtensions: ["application/pdf"],
  });

  const validationSchema = yup.object({
    file: yup.lazy(() => {
      const schema = yup.string();
      if (isRequired) {
        return schema.required();
      }
      return schema;
    }),
  });

  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    validationSchema,
    onSubmit,
    enableReinitialize: true,
    initialValues: {
      file: initialValues.file || "",
    },
  });

  const isError = errors.file && touched.file;
  const isUploadProcessing = () =>
    uploader.isLoading || isUploadProcessingRef.current;
  const isFileProcessing = isUploadProcessing();

  const onFinishLater = () => {
    if (isUploadProcessing()) {
      return;
    }
    onSubmit(values, { saveForLater: true });
  };

  const onFileChange = async (e) => {
    isUploadProcessingRef.current = true;

    try {
      const result = await uploader.htmlOnChange(e);

      if (result?.uploadedFileFullPath) {
        setFieldValue("file", result.uploadedFileFullPath);
      }
    } finally {
      isUploadProcessingRef.current = false;
    }
  };

  const onFooterSubmit = (...args) => {
    if (isUploadProcessing()) {
      return;
    }
    return handleSubmit(...args);
  };

  return (
    <div className={classes.root}>
      <ImageUploader
        name="answerFile"
        value={values.file}
        onRemove={() => setFieldValue("file", "")}
        isLoading={uploader.isLoading}
        onChange={onFileChange}
      />
      {renderFooter(onFooterSubmit, isError, onFinishLater, isFileProcessing)}
    </div>
  );
}

AnswerFile.propTypes = {
  isRequired: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    file: PropTypes.string,
  }).isRequired,
  renderFooter: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
